import { Badge, BadgeType } from '@/app/models/badges'
import { FormBase, PointsAssignmentForm } from '@/app/models/new-activity-form'
import { StoreProps } from '@/app/models/store'
import { selectActiveActivity } from '@/app/redux/selector/activity-creation'
import { ProfileService } from '@/app/services/ProfileService'
import { LuxSelect, LuxTextInput, LUX_INPUT_TYPE } from '@lux/wardrobe-lib'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import SectionItem from '../../Layout/SectionItem/SectionItem'
import SectionItemContainer from '../../Layout/SectionItemContainer/SectionItemContainer'
import './PointsAssignments.scss'

interface PointsAssignmentsProps extends FormBase<PointsAssignmentForm> {
    storeIds?: StoreProps[]
}

export const PointsAssignments = ({
    form,
    isDone,
    storeIds,
    onSetForm,
}: PointsAssignmentsProps) => {
    const intl = useIntl()
    const projectType = useSelector(selectActiveActivity)
    const [options, setOptions] = useState<Badge[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const getErrorMessage = () => {
        if (
            Number(form.points.value) < 0 ||
            Number(form.points.value) > Number(form.badge.value?.points)
        )
            return intl.formatMessage({
                id: 'modals.new.activity.points.assignment.maximum.error',
                defaultMessage:
                    'The number of points exceeds the maximum allowed for the selected badge.',
            })
        return ''
    }
    const getErrorClass = () => {
        if (
            Number(form.points.value) < 0 ||
            Number(form.points.value) > Number(form.badge.value?.points)
        )
            return 'has-error'
        return ''
    }

    useEffect(() => {
        ProfileService.GetActivityBadges(storeIds.map((s) => s.store_id), projectType)
            .then(({ data }) => {
                setOptions(data.data)
                if (!!form.badge.value) {
                    onSetForm({
                        badge: {
                            ...form.badge,
                            value: data.data.find((b) => b.id === form.badge.value.id),
                        },
                    })
                }
            })
            .finally(() => setIsLoading(false))
    }, [])

    return (
        <SectionItemContainer hideFooter={true} isDone={isDone} onClear={onSetForm}>
            <div className="smart-modals-new-activity-points-assignment">
                <SectionItem>
                    <LuxSelect
                        value={form.badge.value}
                        onChangeCallback={(value) => {
                            onSetForm({
                                badge: { ...form.badge, value },
                                points: { ...form.points, value: value.points },
                            })
                        }}
                        mandatory
                        label={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.badge.title',
                            defaultMessage: 'Choose a badge',
                        })}
                        description={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.badge.description',
                            defaultMessage: 'Choose which badge to assign the points to.',
                        })}
                        options={options}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.badge.title',
                            defaultMessage: 'Choose a badge',
                        })}
                        isLoading={isLoading}
                        containerClasses={getErrorClass()}
                        additionalProps={{
                            appendTo: 'self',
                            optionLabel: 'name',
                        }}
                    />
                </SectionItem>
                <SectionItem>
                    <LuxTextInput
                        type={LUX_INPUT_TYPE.number}
                        value={form.points.value}
                        onChangeCallback={(value) => {
                            onSetForm({ points: { ...form.points, value } })
                        }}
                        label={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.points.title',
                            defaultMessage: 'Points',
                        })}
                        description={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.points.description',
                            defaultMessage:
                                "Choose the amount of points you'll earn once you've completed an activity. TIP: 1 point is 1 minute.",
                        })}
                        mandatory
                        disabled={!form.badge.value}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.points.assignment.points.title',
                            defaultMessage: 'Points',
                        })}
                        errorMessage={getErrorMessage()}
                    />
                </SectionItem>
            </div>
        </SectionItemContainer>
    )
}
